import {
  DEFAULT_ADMIN_BFF_BASE_URL,
  MAX_PRESTAGING_SLOT,
  STAGING_CYCLE_STORAGE_KEY,
  STAGING_CYCLE_TOKEN_STORAGE_KEY,
  STAGING_PROJECT_NAME_STORAGE_KEY,
  STAGING_SLOT_STORAGE_KEY,
  STAGING_VALIDATE_TOKEN_PATH,
} from "./constants";

/**
 * Thin, SSR-safe wrapper around sessionStorage for the staging cycle id.
 *
 * <p>Kept as module-level functions (not a class) so the fetch interceptor can
 * import from a tiny module at bootstrap without pulling in React.
 *
 * <p>Under Gatsby SSR (`typeof window === "undefined"`) every function is a
 * no-op / returns null — the build doesn't have a browser session to read.
 */

const isBrowser = (): boolean => typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";

/** @return current cycle id, or null if not in staging mode or running under SSR. */
export function readCycleId(): string | null {
  if (!isBrowser()) return null;
  try {
    return window.sessionStorage.getItem(STAGING_CYCLE_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Persist the given cycle id for the life of this browser session. */
export function writeCycleId(cycleId: string): void {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.setItem(STAGING_CYCLE_STORAGE_KEY, cycleId);
  } catch {
    // sessionStorage can throw on quota or private-browsing restrictions — fail silently.
  }
}

/** Clear the stored cycle id — caller is responsible for any reload. */
export function clearCycleId(): void {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.removeItem(STAGING_CYCLE_STORAGE_KEY);
    // Clear the HMAC token alongside the id — they share a lifecycle.
    window.sessionStorage.removeItem(STAGING_CYCLE_TOKEN_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * @return the stored HMAC-signed staging token, or null when the session has
 *         not received one (legacy {@code ?id=} flow) or is running under SSR.
 */
export function readCycleToken(): string | null {
  if (!isBrowser()) return null;
  try {
    return window.sessionStorage.getItem(STAGING_CYCLE_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Persist the HMAC token alongside the cycle id for the life of this session. */
export function writeCycleToken(token: string): void {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.setItem(STAGING_CYCLE_TOKEN_STORAGE_KEY, token);
  } catch {
    // sessionStorage can throw on quota or private-browsing restrictions — fail silently.
  }
}

// ---------------------------------------------------------------------------
// Active project slot — n-projects preview (May 2026).
// ---------------------------------------------------------------------------

/**
 * @return the active pre-staging project slot (1..{@link MAX_PRESTAGING_SLOT}),
 *         or {@code null} when the session isn't previewing a project (live or
 *         plain staging). Parsed strictly — anything outside the valid range
 *         (or non-integer) returns null, so the fetch interceptor never emits a
 *         malformed slot header.
 */
export function readSlot(): number | null {
  if (!isBrowser()) return null;
  try {
    const raw = window.sessionStorage.getItem(STAGING_SLOT_STORAGE_KEY);
    if (raw === null || raw === "") return null;
    const parsed = Number.parseInt(raw, 10);
    if (!Number.isInteger(parsed) || parsed < 1 || parsed > MAX_PRESTAGING_SLOT) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Persist the active project slot for the life of this browser session. */
export function writeSlot(slot: number): void {
  if (!isBrowser()) return;
  // Upper bound is the deployable ceiling; prod runs 10 slots, local/UAT 5.
  if (!Number.isInteger(slot) || slot < 1 || slot > MAX_PRESTAGING_SLOT) return;
  try {
    window.sessionStorage.setItem(STAGING_SLOT_STORAGE_KEY, String(slot));
  } catch {
    // ignore quota / private-browsing failures
  }
}

/** Clear the active slot — caller is responsible for any reload. */
export function clearSlot(): void {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.removeItem(STAGING_SLOT_STORAGE_KEY);
    // Project name is bound to the slot — when the slot goes, the name
    // must go too so the banner doesn't show a stale label.
    window.sessionStorage.removeItem(STAGING_PROJECT_NAME_STORAGE_KEY);
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Project name — paired with the slot above. Written at /staging entry from
// the &project= URL param, read by the preview banner to display the
// tier-and-name identity.
// ---------------------------------------------------------------------------

/**
 * @return the active pre-staging project's human-readable name (e.g. 'Sale
 *         2026'), or {@code null} when there's no active project (no slot
 *         set, slot set without name, or under SSR).
 */
export function readProjectName(): string | null {
  if (!isBrowser()) return null;
  try {
    const raw = window.sessionStorage.getItem(STAGING_PROJECT_NAME_STORAGE_KEY);
    return raw && raw.length > 0 ? raw : null;
  } catch {
    return null;
  }
}

/** Persist the active project's name alongside its slot for the session. */
export function writeProjectName(name: string): void {
  if (!isBrowser()) return;
  if (typeof name !== "string" || name.length === 0) return;
  try {
    window.sessionStorage.setItem(STAGING_PROJECT_NAME_STORAGE_KEY, name);
  } catch {
    // ignore quota / private-browsing failures
  }
}

// ---------------------------------------------------------------------------
// Full exit cleanup — flush EVERY staging/slot key in one call.
// ---------------------------------------------------------------------------

/**
 * Flush the entire staging-preview session: the staging cycle id + HMAC token
 * AND the pre-staging project slot + project name.
 *
 * <p><b>Why this exists.</b> The staging cycle and the project slot have two
 * independent lifecycles ({@link clearCycleId} vs {@link clearSlot}). "Return
 * to Live" must end BOTH — clearing only the cycle leaves
 * {@code germanos.staging.slot} behind, so the preview banner disappears (it
 * gates on the cycle id) while the fetch/XHR interceptors keep reading the slot
 * and stamping {@code X-Project-Slot} on every API call. That leaks a slot's
 * data (e.g. its promotions) into the "live" session in the same tab until the
 * tab is closed. This helper is the single source of truth for "exit means
 * everything goes", so a future key added to either lifecycle can't be
 * forgotten by the exit path.
 */
export function clearStagingSession(): void {
  clearCycleId();
  clearSlot();
}

/** Shape returned by admin-bff's {@code /validate-token} endpoint. */
export interface TokenValidationResponse {
  valid: boolean;
  cycleId: string | null;
  reason: string | null;
}

/**
 * Resolve the admin-bff base URL at runtime. Mirrors the pattern used by
 * {@code lib/cart-api.ts#getApiBaseUrl} so both endpoints follow one
 * convention for env substitution — matches what every other storefront UI
 * call does (cart, products, etc.):
 * <ol>
 *   <li>{@code window.__BFF_BASE__} — runtime-injected placeholder if any.</li>
 *   <li>{@code process.env.GATSBY_CLIENT_TARGET} — build-time override
 *       pointing at grm-bff (storefront BFF). Same env var the rest of the
 *       app uses; one source of truth.</li>
 *   <li>{@link DEFAULT_ADMIN_BFF_BASE_URL} — dev fallback
 *       ({@code http://localhost:4002}). The serve.js runtime substitution
 *       rewrites this string to {@code GATSBY_CLIENT_TARGET} for deployed
 *       bundles, so no env var name mismatch slips through to the browser.</li>
 * </ol>
 */
function resolveAdminBffBaseUrl(): string {
  if (typeof window !== "undefined") {
    const runtime = (window as unknown as { __BFF_BASE__?: string }).__BFF_BASE__;
    if (typeof runtime === "string" && runtime.length > 0 && runtime !== "__GATSBY_CLIENT_TARGET__") {
      return runtime;
    }
  }
  if (typeof process !== "undefined" && process.env && process.env.GATSBY_CLIENT_TARGET) {
    return process.env.GATSBY_CLIENT_TARGET;
  }
  return DEFAULT_ADMIN_BFF_BASE_URL;
}

/**
 * Call admin-bff's {@code GET /api/admin/staging/cycle/validate-token?t=...}
 * endpoint and return a normalised result. The response body is
 * {@code {valid, cycleId, reason}} — see
 * {@code StagingCycleController#validateToken} in grm-admin-bff.
 *
 * <p>Never throws. A network error or malformed payload is reported as
 * {@code {valid: false, cycleId: null, reason: "network_error"}} so callers
 * can render a single "Invalid preview link" state without a try/catch.
 *
 * <p>SSR-safe: returns a rejected-valid payload immediately when
 * {@code window} is absent.
 *
 * @param token the HMAC-signed preview token from {@code ?t=<...>}
 * @param signal optional abort signal to cancel a pending request when the
 *               component unmounts before the round-trip completes
 */
export async function validateCycleToken(
  token: string,
  signal?: AbortSignal
): Promise<TokenValidationResponse> {
  if (!isBrowser()) {
    return { valid: false, cycleId: null, reason: "ssr" };
  }
  const url = `${resolveAdminBffBaseUrl()}${STAGING_VALIDATE_TOKEN_PATH}?t=${encodeURIComponent(token)}`;
  try {
    const response = await fetch(url, {
      method: "GET",
      credentials: "omit",
      cache: "no-store",
      signal,
    });
    if (!response.ok) {
      return { valid: false, cycleId: null, reason: `http_${response.status}` };
    }
    const raw: unknown = await response.json();
    if (raw === null || typeof raw !== "object") {
      return { valid: false, cycleId: null, reason: "malformed" };
    }
    const payload = raw as Record<string, unknown>;
    const isValid = payload.valid === true;
    const rawCycleId = payload.cycleId;
    const rawReason = payload.reason;
    return {
      valid: isValid,
      cycleId: typeof rawCycleId === "string" ? rawCycleId : null,
      reason: typeof rawReason === "string" ? rawReason : null,
    };
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      return { valid: false, cycleId: null, reason: "aborted" };
    }
    return { valid: false, cycleId: null, reason: "network_error" };
  }
}
